export const normalizeMenuSearchText = (text) =>
  String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const singularizeWord = (word) => {
  if (word.length <= 3) return word;
  if (word === "paises") return "pais";
  if (word.endsWith("oes") || word.endsWith("aes"))
    return `${word.slice(0, -3)}ao`;
  if (word.endsWith("aos")) return word.slice(0, -1);
  if (/[aeou]is$/.test(word)) return `${word.slice(0, -2)}l`;
  if (word.endsWith("ns")) return `${word.slice(0, -2)}m`;
  if (word.endsWith("ores")) return word.slice(0, -2);
  if (word.endsWith("s")) return word.slice(0, -1);
  return word;
};

const textVariations = (text) => {
  const normalized = normalizeMenuSearchText(text);
  const singular = normalized.split(" ").map(singularizeWord).join(" ");
  return [...new Set([normalized, singular])].filter(Boolean);
};

// O índice só muda quando o menu, o idioma ou os acessos mudam.
export const createMenuSearchIndex = (
  items = [],
  { translate = (title) => title, isAvailable = () => true } = {}
) => {
  const results = [];
  const visit = (entries, parents = [], path = []) => {
    if (!Array.isArray(entries)) return;

    entries.forEach((item, index) => {
      // A restrição de um grupo também vale para todos os seus descendentes.
      if (!item || !isAvailable(item)) return;

      const label = String(translate(item.title) || "");
      const titles = [...parents, label];
      const itemPath = [...path, index];
      if (typeof item.link === "string" && item.link.trim()) {
        results.push({
          ...item,
          searchKey: itemPath.join("-"),
          label,
          contextLabel: parents.filter(Boolean).join(" / "),
          titleKeys: textVariations(label),
          searchKeys: [label, item.title, titles.join(" ")].flatMap(
            textVariations
          ),
        });
      }

      visit(item.items || item.children, titles, itemPath);
    });
  };

  visit(items);
  return results;
};

const matchesTokens = (keys, tokens) =>
  tokens.every(([term, singular]) =>
    keys.some(
      (key) =>
        key.includes(term) || (singular && key.split(" ").includes(singular))
    )
  );

export const highlightMenuSearchText = (text, query) => {
  const tokens = normalizeMenuSearchText(query).split(" ").filter(Boolean);
  return String(text || "")
    .split(/(\s+|[/–—-])/)
    .filter(Boolean)
    .map((part) => ({
      text: part,
      match:
        tokens.length > 0 &&
        tokens.some((token) =>
          matchesTokens(textVariations(part), [textVariations(token)])
        ),
    }));
};

export const searchMenuItems = (index, query) => {
  const normalized = normalizeMenuSearchText(query);
  // Campo vazio, null ou apenas espaços/pontuação nunca retorna o menu inteiro.
  if (!normalized) return [];

  const phrases = textVariations(normalized);
  const tokens = normalized.split(" ").map(textVariations);
  const score = (item) => {
    if (item.titleKeys[0] === normalized) return 0;
    if (item.titleKeys.some((key) => phrases.includes(key))) return 1;
    if (
      item.titleKeys.some((key) =>
        phrases.some((phrase) => key.startsWith(`${phrase} `))
      )
    )
      return 2;

    const words = new Set(item.titleKeys.flatMap((key) => key.split(" ")));
    if (
      tokens.every((variations) => variations.some((term) => words.has(term)))
    )
      return 3;
    if (item.titleKeys.some((key) => phrases.some((p) => key.startsWith(p))))
      return 4;
    if (matchesTokens(item.titleKeys, tokens)) return 5;
    return 6;
  };

  return index
    .filter((item) => matchesTokens(item.searchKeys, tokens))
    .map((item) => ({
      item,
      score: score(item),
      wordCount: (item.titleKeys[0] || "").split(" ").length,
      titleLength: (item.titleKeys[0] || "").length,
    }))
    .sort(
      (a, b) =>
        a.score - b.score ||
        // Nos empates pelo título, o nome mais direto vem antes dos complementos.
        (a.score < 6
          ? a.wordCount - b.wordCount || a.titleLength - b.titleLength
          : 0) ||
        a.item.label.localeCompare(b.item.label, undefined, {
          sensitivity: "base",
          numeric: true,
        }) ||
        a.item.contextLabel.localeCompare(b.item.contextLabel)
    )
    .map(({ item }) => item);
};
